import { usePageHeader } from "contexts/PageHeaderContext";
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { 
  Mail, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Settings, 
  CheckCircle2, 
  XCircle,
  Power,
  Users,
  FileCheck,
  UserCheck,
  Globe,
  Send,
  Eye,
  Clock,
  MousePointerClick,
  RefreshCw,
  AlertTriangle,
  History,
  PlayCircle,
  Inbox,
  TrendingUp,
  // Filter,
  // Calendar,
  Paperclip,
  Zap,
  // Link2,
  BarChart3,
  Activity,
  // Bell,
  Database,
  // FileText,
  Layers,
  Star
} from 'lucide-react';

interface EmailConfig {
  id: string;
  module: string;
  process: string;
  emailTemplate: string;
  sendTo: string[];
  sendCC: string[];
  sendBCC: string[];
  isActive: boolean;
  triggerTiming: 'immediate' | 'scheduled' | 'manual';
  priority: 'high' | 'normal' | 'low';
  enableTracking: boolean;
  enableOpenTracking: boolean;
  enableClickTracking: boolean;
  enableBounceTracking: boolean;
  retryAttempts: number;
  retryDelay: number;
  attachments: string[];
  dynamicVariables: string[];
  createdAt: string;
  updatedAt: string;
  emailsSent?: number;
  emailsOpened?: number;
  emailsClicked?: number;
  emailsBounced?: number;
}

interface EmailConfigPageProps {
  onNavigate: (page: string) => void;
}

export const EmailConfigPage = ({ onNavigate }: EmailConfigPageProps) => {
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: "Email Configuration Master",
      childTitle: "Manage email triggers, tracking & analytics",
      icon: <Settings className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<EmailConfig | null>(null);
  const [filterModule, setFilterModule] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [currentTab, setCurrentTab] = useState<'list' | 'analytics'>('list');

  // Form state
  const [formData, setFormData] = useState({
    module: '',
    process: '',
    emailTemplate: '',
    sendTo: [] as string[],
    sendCC: [] as string[],
    sendBCC: [] as string[],
    triggerTiming: 'immediate' as 'immediate' | 'scheduled' | 'manual',
    priority: 'normal' as 'high' | 'normal' | 'low',
    isActive: true,
    enableTracking: true,
    enableOpenTracking: true,
    enableClickTracking: true,
    enableBounceTracking: true,
    retryAttempts: 3,
    retryDelay: 5,
    attachments: [] as string[],
    dynamicVariables: [] as string[]
  });

  // Module definitions with their processes
  const modules = [
    {
      id: 'tour-operator',
      name: 'Tour Operator Management',
      icon: Users,
      processes: [
        'Tour Operator Registration',
        'Tour Operator Approval',
        'Tour Operator Rejection',
        'Tour Operator Profile Update',
        'Tour Operator Suspension',
        'Tour Operator Reactivation'
      ]
    },
    {
      id: 'visa',
      name: 'Visa Management',
      icon: Globe,
      processes: [
        'Visa Application Submitted',
        'Visa Application Approved',
        'Visa Application Rejected',
        'Visa Status Updated',
        'Visa Documents Requested',
        'Visa Payment Received',
        'Visa Ready for Collection'
      ]
    },
    {
      id: 'applicant',
      name: 'Applicant Management',
      icon: UserCheck,
      processes: [
        'Applicant Added',
        'Applicant Profile Updated',
        'Applicant Documents Uploaded',
        'Applicant Verification Complete',
        'Applicant ITS Verification Failed',
        'Bulk Applicants Import Complete'
      ]
    },
    {
      id: 'muwafiqa',
      name: 'Muwafiqa Management',
      icon: FileCheck,
      processes: [
        'Muwafiqa Application Submitted',
        'Muwafiqa Approved',
        'Muwafiqa Rejected',
        'Muwafiqa Documents Pending',
        'Muwafiqa Payment Received',
        'Muwafiqa Expiry Reminder'
      ]
    },
    {
      id: 'finance',
      name: 'Finance Management',
      icon: Send,
      processes: [
        'Advance Payment Received',
        'Payment Confirmation',
        'Invoice Generated',
        'Receipt Issued',
        'Refund Processed',
        'Payment Reminder',
        'Outstanding Balance Alert'
      ]
    }
  ];

  // Available email templates
  const emailTemplates = [
    'Tour Operator Welcome Email',
    'Tour Operator Approval Notification',
    'Tour Operator Rejection Notice',
    'Visa Approval Notification',
    'Visa Rejection Notice',
    'Visa Status Update',
    'Applicant Welcome Email',
    'Applicant Verification Email',
    'Document Request Email',
    'Payment Confirmation Email',
    'Payment Receipt Email',
    'Payment Reminder Email',
    'General Notification',
    'System Alert Email',
    'Muwafiqa Approval Email',
    'Muwafiqa Rejection Email'
  ];

  // Recipient options
  const recipientOptions = [
    'Tour Operator',
    'Applicant',
    'Super Admin',
    'Finance Team',
    'Visa Processing Team',
    'Document Verification Team',
    'Custom Email'
  ];

  // Available dynamic variables
  const availableDynamicVariables = [
    '{{user_name}}',
    '{{user_email}}',
    '{{user_its}}',
    '{{tour_operator_name}}',
    '{{application_id}}',
    '{{application_status}}',
    '{{payment_amount}}',
    '{{payment_date}}',
    '{{visa_number}}',
    '{{document_name}}',
    '{{due_date}}',
    '{{system_date}}',
    '{{approval_date}}',
    '{{rejection_reason}}'
  ];

  // Mock data with tracking stats
  const mockConfigs: EmailConfig[] = [
    {
      id: '1',
      module: 'Tour Operator Management',
      process: 'Tour Operator Approval',
      emailTemplate: 'Tour Operator Approval Notification',
      sendTo: ['Tour Operator'],
      sendCC: ['Super Admin'],
      sendBCC: [],
      isActive: true,
      triggerTiming: 'immediate',
      priority: 'high',
      enableTracking: true,
      enableOpenTracking: true,
      enableClickTracking: true,
      enableBounceTracking: true,
      retryAttempts: 3,
      retryDelay: 5,
      attachments: ['Terms & Conditions.pdf'],
      dynamicVariables: ['{{user_name}}', '{{approval_date}}'],
      createdAt: '2025-01-01',
      updatedAt: '2025-01-05',
      emailsSent: 145,
      emailsOpened: 132,
      emailsClicked: 89,
      emailsBounced: 3
    },
    {
      id: '2',
      module: 'Visa Management',
      process: 'Visa Application Approved',
      emailTemplate: 'Visa Approval Notification',
      sendTo: ['Tour Operator', 'Applicant'],
      sendCC: ['Visa Processing Team'],
      sendBCC: [],
      isActive: true,
      triggerTiming: 'immediate',
      priority: 'high',
      enableTracking: true,
      enableOpenTracking: true,
      enableClickTracking: true,
      enableBounceTracking: true,
      retryAttempts: 3,
      retryDelay: 5,
      attachments: [],
      dynamicVariables: ['{{user_name}}', '{{visa_number}}', '{{approval_date}}'],
      createdAt: '2025-01-02',
      updatedAt: '2025-01-05',
      emailsSent: 428,
      emailsOpened: 401,
      emailsClicked: 312,
      emailsBounced: 7
    },
    {
      id: '3',
      module: 'Applicant Management',
      process: 'Applicant Added',
      emailTemplate: 'Applicant Welcome Email',
      sendTo: ['Applicant'],
      sendCC: ['Tour Operator'],
      sendBCC: [],
      isActive: true,
      triggerTiming: 'immediate',
      priority: 'normal',
      enableTracking: true,
      enableOpenTracking: true,
      enableClickTracking: true,
      enableBounceTracking: true,
      retryAttempts: 3,
      retryDelay: 5,
      attachments: ['Welcome Guide.pdf'],
      dynamicVariables: ['{{user_name}}', '{{user_its}}', '{{tour_operator_name}}'],
      createdAt: '2025-01-03',
      updatedAt: '2025-01-05',
      emailsSent: 892,
      emailsOpened: 756,
      emailsClicked: 543,
      emailsBounced: 12
    },
    {
      id: '4',
      module: 'Finance Management',
      process: 'Advance Payment Received',
      emailTemplate: 'Payment Confirmation Email',
      sendTo: ['Tour Operator'],
      sendCC: ['Finance Team'],
      sendBCC: ['Super Admin'],
      isActive: true,
      triggerTiming: 'immediate',
      priority: 'high',
      enableTracking: true,
      enableOpenTracking: true,
      enableClickTracking: false,
      enableBounceTracking: true,
      retryAttempts: 5,
      retryDelay: 10,
      attachments: ['Payment Receipt.pdf', 'Invoice.pdf'],
      dynamicVariables: ['{{user_name}}', '{{payment_amount}}', '{{payment_date}}'],
      createdAt: '2025-01-04',
      updatedAt: '2025-01-05',
      emailsSent: 324,
      emailsOpened: 318,
      emailsClicked: 0,
      emailsBounced: 2
    },
    {
      id: '5',
      module: 'Visa Management',
      process: 'Visa Application Rejected',
      emailTemplate: 'Visa Rejection Notice',
      sendTo: ['Tour Operator', 'Applicant'],
      sendCC: ['Visa Processing Team'],
      sendBCC: [],
      isActive: false,
      triggerTiming: 'immediate',
      priority: 'high',
      enableTracking: true,
      enableOpenTracking: true,
      enableClickTracking: true,
      enableBounceTracking: true,
      retryAttempts: 3,
      retryDelay: 5,
      attachments: [],
      dynamicVariables: ['{{user_name}}', '{{rejection_reason}}'],
      createdAt: '2025-01-05',
      updatedAt: '2025-01-05',
      emailsSent: 23,
      emailsOpened: 22,
      emailsClicked: 8,
      emailsBounced: 1
    },
    {
      id: '6',
      module: 'Finance Management',
      process: 'Payment Reminder',
      emailTemplate: 'Payment Reminder Email',
      sendTo: ['Tour Operator'],
      sendCC: ['Finance Team'],
      sendBCC: [],
      isActive: true,
      triggerTiming: 'scheduled',
      priority: 'normal',
      enableTracking: true,
      enableOpenTracking: true,
      enableClickTracking: true,
      enableBounceTracking: true,
      retryAttempts: 2,
      retryDelay: 15,
      attachments: [],
      dynamicVariables: ['{{user_name}}', '{{payment_amount}}', '{{due_date}}'],
      createdAt: '2025-01-08',
      updatedAt: '2025-01-08',
      emailsSent: 67,
      emailsOpened: 54,
      emailsClicked: 31,
      emailsBounced: 4
    }
  ];

  const [configs, setConfigs] = useState<EmailConfig[]>(mockConfigs);

  const filteredConfigs = configs.filter(config => {
    const matchesSearch = 
      config.module.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.process.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.emailTemplate.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesModule = filterModule === 'all' || config.module === filterModule;
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && config.isActive) ||
      (filterStatus === 'inactive' && !config.isActive);
    const matchesPriority = filterPriority === 'all' || config.priority === filterPriority;

    return matchesSearch && matchesModule && matchesStatus && matchesPriority;
  });

  // Calculate analytics
  const totalEmailsSent = configs.reduce((sum, config) => sum + (config.emailsSent || 0), 0);
  const totalEmailsOpened = configs.reduce((sum, config) => sum + (config.emailsOpened || 0), 0);
  const totalEmailsClicked = configs.reduce((sum, config) => sum + (config.emailsClicked || 0), 0);
  const totalEmailsBounced = configs.reduce((sum, config) => sum + (config.emailsBounced || 0), 0);
  const openRate = totalEmailsSent > 0 ? ((totalEmailsOpened / totalEmailsSent) * 100).toFixed(1) : '0';
  const clickRate = totalEmailsOpened > 0 ? ((totalEmailsClicked / totalEmailsOpened) * 100).toFixed(1) : '0';
  const bounceRate = totalEmailsSent > 0 ? ((totalEmailsBounced / totalEmailsSent) * 100).toFixed(1) : '0';

  const handleAddConfig = () => {
    const newConfig: EmailConfig = {
      id: `config-${Date.now()}`,
      module: formData.module,
      process: formData.process,
      emailTemplate: formData.emailTemplate,
      sendTo: formData.sendTo,
      sendCC: formData.sendCC,
      sendBCC: formData.sendBCC,
      isActive: formData.isActive,
      triggerTiming: formData.triggerTiming,
      priority: formData.priority,
      enableTracking: formData.enableTracking,
      enableOpenTracking: formData.enableOpenTracking,
      enableClickTracking: formData.enableClickTracking,
      enableBounceTracking: formData.enableBounceTracking,
      retryAttempts: formData.retryAttempts,
      retryDelay: formData.retryDelay,
      attachments: formData.attachments,
      dynamicVariables: formData.dynamicVariables,
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0],
      emailsSent: 0,
      emailsOpened: 0,
      emailsClicked: 0,
      emailsBounced: 0
    };

    setConfigs([...configs, newConfig]);
    setShowAddModal(false);
    resetForm();
  };

  const handleEditConfig = () => {
    if (!selectedConfig) return;

    const updatedConfigs = configs.map(config => 
      config.id === selectedConfig.id 
        ? {
            ...config,
            module: formData.module,
            process: formData.process,
            emailTemplate: formData.emailTemplate,
            sendTo: formData.sendTo,
            sendCC: formData.sendCC,
            sendBCC: formData.sendBCC,
            isActive: formData.isActive,
            triggerTiming: formData.triggerTiming,
            priority: formData.priority,
            enableTracking: formData.enableTracking,
            enableOpenTracking: formData.enableOpenTracking,
            enableClickTracking: formData.enableClickTracking,
            enableBounceTracking: formData.enableBounceTracking,
            retryAttempts: formData.retryAttempts,
            retryDelay: formData.retryDelay,
            attachments: formData.attachments,
            dynamicVariables: formData.dynamicVariables,
            updatedAt: new Date().toISOString().split('T')[0]
          }
        : config
    );

    setConfigs(updatedConfigs);
    setShowEditModal(false);
    setSelectedConfig(null);
    resetForm();
  };

  // const handleDeleteConfig = (id: string) => {
  //   if (confirm('Are you sure you want to delete this email configuration?')) {
  //     setConfigs(configs.filter(config => config.id !== id));
  //   }
  // };

  const handleToggleStatus = (id: string) => {
    setConfigs(configs.map(config => 
      config.id === id 
        ? { ...config, isActive: !config.isActive }
        : config
    ));
  };

  const resetForm = () => {
    setFormData({
      module: '',
      process: '',
      emailTemplate: '',
      sendTo: [],
      sendCC: [],
      sendBCC: [],
      triggerTiming: 'immediate',
      priority: 'normal',
      isActive: true,
      enableTracking: true,
      enableOpenTracking: true,
      enableClickTracking: true,
      enableBounceTracking: true,
      retryAttempts: 3,
      retryDelay: 5,
      attachments: [],
      dynamicVariables: []
    });
  };

  const openEditModal = (config: EmailConfig) => {
    setSelectedConfig(config);
    setFormData({
      module: config.module,
      process: config.process,
      emailTemplate: config.emailTemplate,
      sendTo: config.sendTo,
      sendCC: config.sendCC,
      sendBCC: config.sendBCC,
      triggerTiming: config.triggerTiming,
      priority: config.priority,
      isActive: config.isActive,
      enableTracking: config.enableTracking,
      enableOpenTracking: config.enableOpenTracking,
      enableClickTracking: config.enableClickTracking,
      enableBounceTracking: config.enableBounceTracking,
      retryAttempts: config.retryAttempts,
      retryDelay: config.retryDelay,
      attachments: config.attachments,
      dynamicVariables: config.dynamicVariables
    });
    setShowEditModal(true);
  };

  const openDetailsModal = (config: EmailConfig) => {
    setSelectedConfig(config);
    setShowDetailsModal(true);
  };

  const openLogsModal = (config: EmailConfig) => {
    setSelectedConfig(config);
    setShowLogsModal(true);
  };

  const getModuleIcon = (moduleName: string) => {
    const module = modules.find(m => m.name === moduleName);
    return module?.icon || Mail;
  };

  const ConfigForm = ({ isEdit = false }: { isEdit?: boolean }) => (
    <div className="space-y-6">
      {/* Basic Information Section */}
      <div className="bg-gradient-to-r from-[#5B9BD5]/10 to-[#5B9BD5]/5 rounded-xl p-4 border border-[#5B9BD5]/20">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Settings className="w-4 h-4" style={{ color: '#5B9BD5' }} />
          Basic Configuration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Module Selection */}
          <div>
            <Label className="text-gray-700">Module *</Label>
            <Select value={formData.module} onValueChange={(value) => {
              setFormData({ ...formData, module: value, process: '' });
            }}>
              <SelectTrigger className="mt-1 border-gray-300">
                <SelectValue placeholder="Select Module" />
              </SelectTrigger>
              <SelectContent>
                {modules.map(module => (
                  <SelectItem key={module.id} value={module.name}>
                    {module.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Process Selection */}
          <div>
            <Label className="text-gray-700">Process/Event *</Label>
            <Select 
              value={formData.process} 
              onValueChange={(value) => setFormData({ ...formData, process: value })}
              disabled={!formData.module}
            >
              <SelectTrigger className="mt-1 border-gray-300">
                <SelectValue placeholder="Select Process" />
              </SelectTrigger>
              <SelectContent>
                {modules.find(m => m.name === formData.module)?.processes.map(process => (
                  <SelectItem key={process} value={process}>
                    {process}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Email Template */}
          <div>
            <Label className="text-gray-700">Email Template *</Label>
            <Select 
              value={formData.emailTemplate} 
              onValueChange={(value) => setFormData({ ...formData, emailTemplate: value })}
            >
              <SelectTrigger className="mt-1 border-gray-300">
                <SelectValue placeholder="Select Email Template" />
              </SelectTrigger>
              <SelectContent>
                {emailTemplates.map(template => (
                  <SelectItem key={template} value={template}>
                    {template}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Trigger Timing */}
          <div>
            <Label className="text-gray-700">Trigger Timing *</Label>
            <Select 
              value={formData.triggerTiming} 
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onValueChange={(value: any) => setFormData({ ...formData, triggerTiming: value })}
            >
              <SelectTrigger className="mt-1 border-gray-300">
                <SelectValue placeholder="Select Trigger Timing" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="immediate">
                  <div className="flex items-center gap-2">
                    <Zap className="w-3 h-3" />
                    Immediate
                  </div>
                </SelectItem>
                <SelectItem value="scheduled">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    Scheduled
                  </div>
                </SelectItem>
                <SelectItem value="manual">
                  <div className="flex items-center gap-2">
                    <PlayCircle className="w-3 h-3" />
                    Manual
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Priority */}
          <div>
            <Label className="text-gray-700">Priority *</Label>
            <Select 
              value={formData.priority} 
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onValueChange={(value: any) => setFormData({ ...formData, priority: value })}
            >
              <SelectTrigger className="mt-1 border-gray-300">
                <SelectValue placeholder="Select Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-3 h-3 text-red-500" />
                    High Priority
                  </div>
                </SelectItem>
                <SelectItem value="normal">
                  <div className="flex items-center gap-2">
                    <Inbox className="w-3 h-3 text-blue-500" />
                    Normal Priority
                  </div>
                </SelectItem>
                <SelectItem value="low">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-3 h-3 text-gray-500" />
                    Low Priority
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Recipients Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-600" />
          Email Recipients
        </h3>

        {/* Send To (Primary Recipients) */}
        <div className="mb-4">
          <Label className="text-gray-700 mb-2 block">Send To (Primary Recipients) *</Label>
          <div className="grid grid-cols-2 gap-2">
            {recipientOptions.map(recipient => (
              <label key={recipient} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-gray-200 cursor-pointer hover:bg-blue-50 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.sendTo.includes(recipient)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData({ ...formData, sendTo: [...formData.sendTo, recipient] });
                    } else {
                      setFormData({ ...formData, sendTo: formData.sendTo.filter(r => r !== recipient) });
                    }
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">{recipient}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Send CC */}
        <div className="mb-4">
          <Label className="text-gray-700 mb-2 block">CC (Carbon Copy)</Label>
          <div className="grid grid-cols-2 gap-2">
            {recipientOptions.map(recipient => (
              <label key={recipient} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-gray-200 cursor-pointer hover:bg-yellow-50 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.sendCC.includes(recipient)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData({ ...formData, sendCC: [...formData.sendCC, recipient] });
                    } else {
                      setFormData({ ...formData, sendCC: formData.sendCC.filter(r => r !== recipient) });
                    }
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">{recipient}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Send BCC */}
        <div>
          <Label className="text-gray-700 mb-2 block">BCC (Blind Carbon Copy)</Label>
          <div className="grid grid-cols-2 gap-2">
            {recipientOptions.map(recipient => (
              <label key={recipient} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.sendBCC.includes(recipient)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData({ ...formData, sendBCC: [...formData.sendBCC, recipient] });
                    } else {
                      setFormData({ ...formData, sendBCC: formData.sendBCC.filter(r => r !== recipient) });
                    }
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">{recipient}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Email Tracking Options */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Activity className="w-4 h-4 text-green-600" />
          Email Tracking & Analytics
        </h3>
        <div className="space-y-3">
          <label className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-green-50 transition-colors">
            <input
              type="checkbox"
              checked={formData.enableTracking}
              onChange={(e) => setFormData({ ...formData, enableTracking: e.target.checked })}
              className="rounded border-gray-300"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-900">Enable Email Tracking</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Track all email activities and generate analytics</p>
            </div>
          </label>

          <label className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-green-50 transition-colors">
            <input
              type="checkbox"
              checked={formData.enableOpenTracking}
              onChange={(e) => setFormData({ ...formData, enableOpenTracking: e.target.checked })}
              className="rounded border-gray-300"
              disabled={!formData.enableTracking}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-900">Track Email Opens</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Monitor when recipients open the email</p>
            </div>
          </label>

          <label className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-green-50 transition-colors">
            <input
              type="checkbox"
              checked={formData.enableClickTracking}
              onChange={(e) => setFormData({ ...formData, enableClickTracking: e.target.checked })}
              className="rounded border-gray-300"
              disabled={!formData.enableTracking}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <MousePointerClick className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-gray-900">Track Link Clicks</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Monitor clicks on links within the email</p>
            </div>
          </label>

          <label className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-green-50 transition-colors">
            <input
              type="checkbox"
              checked={formData.enableBounceTracking}
              onChange={(e) => setFormData({ ...formData, enableBounceTracking: e.target.checked })}
              className="rounded border-gray-300"
              disabled={!formData.enableTracking}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-gray-900">Track Email Bounces</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Monitor bounced emails and failed deliveries</p>
            </div>
          </label>
        </div>
      </div>

      {/* Retry Configuration */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-orange-600" />
          Retry Configuration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-700">Maximum Retry Attempts</Label>
            <Input
              type="number"
              min="0"
              max="10"
              value={formData.retryAttempts}
              onChange={(e) => setFormData({ ...formData, retryAttempts: parseInt(e.target.value) || 0 })}
              className="mt-1 border-gray-300"
            />
            <p className="text-xs text-gray-500 mt-1">Number of retry attempts for failed emails</p>
          </div>
          <div>
            <Label className="text-gray-700">Retry Delay (minutes)</Label>
            <Input
              type="number"
              min="1"
              max="60"
              value={formData.retryDelay}
              onChange={(e) => setFormData({ ...formData, retryDelay: parseInt(e.target.value) || 5 })}
              className="mt-1 border-gray-300"
            />
            <p className="text-xs text-gray-500 mt-1">Time to wait before retrying</p>
          </div>
        </div>
      </div>

      {/* Dynamic Variables */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Database className="w-4 h-4 text-purple-600" />
          Dynamic Variables
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {availableDynamicVariables.map(variable => (
            <label key={variable} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-gray-200 cursor-pointer hover:bg-purple-50 transition-colors">
              <input
                type="checkbox"
                checked={formData.dynamicVariables.includes(variable)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({ ...formData, dynamicVariables: [...formData.dynamicVariables, variable] });
                  } else {
                    setFormData({ ...formData, dynamicVariables: formData.dynamicVariables.filter(v => v !== variable) });
                  }
                }}
                className="rounded border-gray-300"
              />
              <span className="text-xs font-mono text-gray-700">{variable}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-3">Select variables to use in email templates for personalization</p>
      </div>

      {/* Status */}
      <div className="flex items-center gap-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
        <input
          type="checkbox"
          id="isActive"
          checked={formData.isActive}
          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
          className="rounded border-gray-300"
        />
        <Label htmlFor="isActive" className="text-gray-700 font-medium">
          Enable this email configuration immediately after saving
        </Label>
      </div>

      {/* Buttons */}
      <div className="flex gap-3 pt-4 border-t border-gray-200">
        <Button
          onClick={isEdit ? handleEditConfig : handleAddConfig}
          className="flex-1 text-white shadow-lg hover:opacity-90"
          style={{ backgroundColor: '#5B9BD5' }}
          disabled={!formData.module || !formData.process || !formData.emailTemplate || formData.sendTo.length === 0}
        >
          <CheckCircle2 className="w-4 h-4 mr-2" />
          {isEdit ? 'Update Configuration' : 'Create Configuration'}
        </Button>
        <Button
          onClick={() => {
            if (isEdit) {
              setShowEditModal(false);
              setSelectedConfig(null);
            } else {
              setShowAddModal(false);
            }
            resetForm();
          }}
          variant="outline"
          className="flex-1"
        >
          <XCircle className="w-4 h-4 mr-2" />
          Cancel
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center gap-4">

        <Button
          onClick={() => setShowAddModal(true)}
          className="text-white shadow-lg hover:opacity-90"
          style={{ backgroundColor: '#5B9BD5' }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Configuration
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setCurrentTab('list')}
          className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${
            currentTab === 'list'
              ? 'border-[#5B9BD5] text-[#5B9BD5]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Configurations
          </div>
        </button>
        <button
          onClick={() => setCurrentTab('analytics')}
          className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${
            currentTab === 'analytics'
              ? 'border-[#5B9BD5] text-[#5B9BD5]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Analytics
          </div>
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-[#5B9BD5] to-[#4A8BC2] text-white shadow-xl hover:shadow-2xl transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4">
            <CardTitle className="text-sm text-white">Total Sent</CardTitle>
            <Send className="w-4 h-4 text-white/80" />
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl text-white">{totalEmailsSent.toLocaleString()}</div>
            <p className="text-xs text-white/70">Emails delivered</p>
          </CardContent>
        </Card>

        <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg hover:shadow-xl transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4">
            <CardTitle className="text-sm" style={{ color: '#5B9BD5' }}>Open Rate</CardTitle>
            <Eye className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl text-gray-900">{openRate}%</div>
            <p className="text-xs text-gray-600">{totalEmailsOpened.toLocaleString()} opened</p>
          </CardContent>
        </Card>

        <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg hover:shadow-xl transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4">
            <CardTitle className="text-sm" style={{ color: '#5B9BD5' }}>Click Rate</CardTitle>
            <MousePointerClick className="w-4 h-4 text-purple-500" />
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl text-gray-900">{clickRate}%</div>
            <p className="text-xs text-gray-600">{totalEmailsClicked.toLocaleString()} clicked</p>
          </CardContent>
        </Card>

        <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg hover:shadow-xl transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4">
            <CardTitle className="text-sm" style={{ color: '#5B9BD5' }}>Bounce Rate</CardTitle>
            <AlertTriangle className="w-4 h-4 text-orange-500" />
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl text-gray-900">{bounceRate}%</div>
            <p className="text-xs text-gray-600">{totalEmailsBounced.toLocaleString()} bounced</p>
          </CardContent>
        </Card>
      </div>

      {currentTab === 'list' && (
        <>
          {/* Filters */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-sm">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Search configurations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-gray-300"
                  />
                </div>
                <Select value={filterModule} onValueChange={setFilterModule}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Filter by Module" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Modules</SelectItem>
                    {modules.map(module => (
                      <SelectItem key={module.id} value={module.name}>
                        {module.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Filter by Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active Only</SelectItem>
                    <SelectItem value="inactive">Inactive Only</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Filter by Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="high">High Priority</SelectItem>
                    <SelectItem value="normal">Normal Priority</SelectItem>
                    <SelectItem value="low">Low Priority</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Configurations List */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg">
            <CardHeader className="border-b border-gray-100">
              <div className="flex items-center justify-between">
                <CardTitle style={{ color: '#5B9BD5' }}>Email Configurations</CardTitle>
                <Badge variant="secondary" className="text-sm">
                  {filteredConfigs.length} Configurations
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#5B9BD5]/10">
                    <tr>
                      <th className="text-left p-4 text-sm text-gray-900">Configuration</th>
                      <th className="text-left p-4 text-sm text-gray-900">Template & Priority</th>
                      <th className="text-center p-4 text-sm text-gray-900">Email Analytics</th>
                      <th className="text-center p-4 text-sm text-gray-900">Tracking</th>
                      <th className="text-center p-4 text-sm text-gray-900">Status</th>
                      <th className="text-center p-4 text-sm text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredConfigs.map((config) => {
                      const ModuleIcon = getModuleIcon(config.module);
                      // const openRate = config.emailsSent ? ((config.emailsOpened! / config.emailsSent) * 100).toFixed(0) : '0';
                      // const clickRate = config.emailsOpened ? ((config.emailsClicked! / config.emailsOpened) * 100).toFixed(0) : '0';

                      return (
                        <tr key={config.id} className="border-b border-gray-100 hover:bg-[#5B9BD5]/5">
                          <td className="p-4">
                            <div className="flex items-start gap-3">
                              <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm"
                                style={{ backgroundColor: '#5B9BD5' }}
                              >
                                <ModuleIcon className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{config.module}</p>
                                <p className="text-xs text-gray-500 mt-1">{config.process}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge variant="outline" className="text-xs">
                                    {config.triggerTiming}
                                  </Badge>
                                  {config.attachments.length > 0 && (
                                    <Badge variant="outline" className="text-xs flex items-center gap-1">
                                      <Paperclip className="w-3 h-3" />
                                      {config.attachments.length}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-900">{config.emailTemplate}</span>
                              </div>
                              <Badge 
                                variant="outline" 
                                className="text-xs"
                                style={{ 
                                  backgroundColor: config.priority === 'high' ? '#FEE2E2' : 
                                                 config.priority === 'normal' ? '#DBEAFE' : '#F3F4F6',
                                  color: config.priority === 'high' ? '#991B1B' : 
                                         config.priority === 'normal' ? '#1E40AF' : '#4B5563',
                                  borderColor: config.priority === 'high' ? '#FCA5A5' : 
                                              config.priority === 'normal' ? '#93C5FD' : '#D1D5DB'
                                }}
                              >
                                {config.priority} priority
                              </Badge>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="space-y-2">
                              <div className="flex items-center justify-center gap-4">
                                <div className="text-center">
                                  <div className="flex items-center gap-1 text-xs text-gray-500">
                                    <Send className="w-3 h-3" />
                                    Sent
                                  </div>
                                  <div className="text-sm font-medium text-gray-900">{config.emailsSent}</div>
                                </div>
                                <div className="text-center">
                                  <div className="flex items-center gap-1 text-xs text-gray-500">
                                    <Eye className="w-3 h-3" />
                                    Opens
                                  </div>
                                  <div className="text-sm font-medium text-blue-600">{openRate}%</div>
                                </div>
                                <div className="text-center">
                                  <div className="flex items-center gap-1 text-xs text-gray-500">
                                    <MousePointerClick className="w-3 h-3" />
                                    Clicks
                                  </div>
                                  <div className="text-sm font-medium text-purple-600">{clickRate}%</div>
                                </div>
                              </div>
                              <Button
                                onClick={() => openDetailsModal(config)}
                                size="sm"
                                variant="ghost"
                                className="w-full text-xs"
                                style={{ color: '#5B9BD5' }}
                              >
                                <BarChart3 className="w-3 h-3 mr-1" />
                                View Details
                              </Button>
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex flex-col items-center gap-1">
                              {config.enableOpenTracking && (
                                <Badge variant="outline" className="text-xs" style={{ backgroundColor: '#DBEAFE', color: '#1E40AF', borderColor: '#93C5FD' }}>
                                  <Eye className="w-3 h-3 mr-1" />
                                  Opens
                                </Badge>
                              )}
                              {config.enableClickTracking && (
                                <Badge variant="outline" className="text-xs" style={{ backgroundColor: '#EDE9FE', color: '#5B21B6', borderColor: '#C4B5FD' }}>
                                  <MousePointerClick className="w-3 h-3 mr-1" />
                                  Clicks
                                </Badge>
                              )}
                              {config.enableBounceTracking && (
                                <Badge variant="outline" className="text-xs" style={{ backgroundColor: '#FEF3C7', color: '#92400E', borderColor: '#FDE68A' }}>
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Bounces
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            <button
                              onClick={() => handleToggleStatus(config.id)}
                              className="inline-flex items-center gap-1"
                            >
                              {config.isActive ? (
                                <Badge className="text-xs cursor-pointer" style={{ backgroundColor: '#10B981', color: 'white' }}>
                                  <Power className="w-3 h-3 mr-1" />
                                  Active
                                </Badge>
                              ) : (
                                <Badge className="text-xs cursor-pointer" style={{ backgroundColor: '#EF4444', color: 'white' }}>
                                  <Power className="w-3 h-3 mr-1" />
                                  Inactive
                                </Badge>
                              )}
                            </button>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                onClick={() => openLogsModal(config)}
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-green-600"
                                title="View Logs"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => openEditModal(config)}
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0"
                                style={{ color: '#5B9BD5' }}
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                // onClick={() => handleDeleteConfig(config.id)}
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {filteredConfigs.length === 0 && (
                  <div className="text-center py-12">
                    <Mail className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600">No email configurations found</p>
                    <p className="text-sm text-gray-500 mt-1">Add a new configuration to get started</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {currentTab === 'analytics' && (
        <div className="space-y-6">
          {/* Analytics Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-xl">
              <CardHeader className="pb-2 p-4">
                <CardTitle className="text-sm text-white/90">Active Configurations</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-3xl text-white mb-2">{configs.filter(c => c.isActive).length}</div>
                <div className="text-xs text-white/70">
                  {configs.length} total configurations
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white shadow-xl">
              <CardHeader className="pb-2 p-4">
                <CardTitle className="text-sm text-white/90">Average Open Rate</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-3xl text-white mb-2">{openRate}%</div>
                <div className="text-xs text-white/70">
                  Industry avg: 21.5%
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-xl">
              <CardHeader className="pb-2 p-4">
                <CardTitle className="text-sm text-white/90">Average Click Rate</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-3xl text-white mb-2">{clickRate}%</div>
                <div className="text-xs text-white/70">
                  Industry avg: 2.6%
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Performing Configurations */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg">
            <CardHeader className="border-b border-gray-100">
              <CardTitle style={{ color: '#5B9BD5' }} className="flex items-center gap-2">
                <Star className="w-5 h-5" />
                Top Performing Configurations
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {configs
                  .filter(c => c.emailsSent && c.emailsSent > 0)
                  // .sort((a, b) => {
                  //   // const rateA = a.emailsOpened! / a.emailsSent!;
                  //   // const rateB = b.emailsOpened! / b.emailsSent!;
                  //   return rateB - rateA;
                  // })
                  .slice(0, 5)
                  .map((config, index) => {
                    // const openRate = ((config.emailsOpened! / config.emailsSent!) * 100).toFixed(1);
                    // const clickRate = config.emailsOpened ? ((config.emailsClicked! / config.emailsOpened) * 100).toFixed(1) : '0';

                    return (
                      <div key={config.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-300">#{index + 1}</div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{config.process}</p>
                          <p className="text-sm text-gray-500">{config.module}</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-4">
                            <div>
                              <div className="text-xs text-gray-500">Open Rate</div>
                              <div className="text-lg font-semibold text-blue-600">{openRate}%</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500">Click Rate</div>
                              <div className="text-lg font-semibold text-purple-600">{clickRate}%</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500">Sent</div>
                              <div className="text-lg font-semibold text-gray-900">{config.emailsSent}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>

          {/* Module Performance */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg">
            <CardHeader className="border-b border-gray-100">
              <CardTitle style={{ color: '#5B9BD5' }} className="flex items-center gap-2">
                <Layers className="w-5 h-5" />
                Performance by Module
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {modules.map(module => {
                  const moduleConfigs = configs.filter(c => c.module === module.name);
                  const totalSent = moduleConfigs.reduce((sum, c) => sum + (c.emailsSent || 0), 0);
                  const totalOpened = moduleConfigs.reduce((sum, c) => sum + (c.emailsOpened || 0), 0);
                  const totalClicked = moduleConfigs.reduce((sum, c) => sum + (c.emailsClicked || 0), 0);
                  const openRate = totalSent > 0 ? ((totalOpened / totalSent) * 100).toFixed(1) : '0';
                  const clickRate = totalOpened > 0 ? ((totalClicked / totalOpened) * 100).toFixed(1) : '0';
                  const ModuleIcon = module.icon;

                  return (
                    <div key={module.id} className="flex items-center gap-4 p-4 bg-gradient-to-r from-[#5B9BD5]/5 to-transparent rounded-lg border border-[#5B9BD5]/10">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#5B9BD5' }}
                      >
                        <ModuleIcon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{module.name}</p>
                        <p className="text-sm text-gray-500">{moduleConfigs.length} configurations</p>
                      </div>
                      <div className="grid grid-cols-4 gap-6 text-center">
                        <div>
                          <div className="text-xs text-gray-500">Sent</div>
                          <div className="text-lg font-semibold text-gray-900">{totalSent}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Opened</div>
                          <div className="text-lg font-semibold text-gray-900">{totalOpened}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Open Rate</div>
                          <div className="text-lg font-semibold text-blue-600">{openRate}%</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Click Rate</div>
                          <div className="text-lg font-semibold text-purple-600">{clickRate}%</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b border-gray-100 sticky top-0 bg-white z-10">
              <CardTitle style={{ color: '#5B9BD5' }} className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Create Email Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ConfigForm />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b border-gray-100 sticky top-0 bg-white z-10">
              <CardTitle style={{ color: '#5B9BD5' }} className="flex items-center gap-2">
                <Edit className="w-5 h-5" />
                Edit Email Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ConfigForm isEdit={true} />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b border-gray-100 sticky top-0 bg-white z-10">
              <CardTitle style={{ color: '#5B9BD5' }} className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Email Analytics Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Configuration Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Configuration Details</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Module:</span>
                    <span className="text-sm font-medium text-gray-900">{selectedConfig.module}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Process:</span>
                    <span className="text-sm font-medium text-gray-900">{selectedConfig.process}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Template:</span>
                    <span className="text-sm font-medium text-gray-900">{selectedConfig.emailTemplate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Priority:</span>
                    <Badge className="text-xs" style={{ 
                      backgroundColor: selectedConfig.priority === 'high' ? '#EF4444' : 
                                     selectedConfig.priority === 'normal' ? '#3B82F6' : '#6B7280',
                      color: 'white'
                    }}>
                      {selectedConfig.priority}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Email Stats */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Email Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
                    <Send className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                    <div className="text-2xl font-bold text-gray-900">{selectedConfig.emailsSent}</div>
                    <div className="text-xs text-gray-600">Emails Sent</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
                    <Eye className="w-6 h-6 mx-auto mb-2 text-green-600" />
                    <div className="text-2xl font-bold text-gray-900">{selectedConfig.emailsOpened}</div>
                    <div className="text-xs text-gray-600">Emails Opened</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center border border-purple-200">
                    <MousePointerClick className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                    <div className="text-2xl font-bold text-gray-900">{selectedConfig.emailsClicked}</div>
                    <div className="text-xs text-gray-600">Links Clicked</div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 text-center border border-orange-200">
                    <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-orange-600" />
                    <div className="text-2xl font-bold text-gray-900">{selectedConfig.emailsBounced}</div>
                    <div className="text-xs text-gray-600">Bounced</div>
                  </div>
                </div>
              </div>

              {/* Rates */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Performance Rates</h3>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-600">Open Rate</span>
                      <span className="text-sm font-medium text-gray-900">
                        {/* {selectedConfig.emailsSent ? ((selectedConfig.emailsOpened! / selectedConfig.emailsSent) * 100).toFixed(1) : '0'}% */}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ 
                          // width: `${selectedConfig.emailsSent ? ((selectedConfig.emailsOpened! / selectedConfig.emailsSent) * 100) : 0}%` 
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-600">Click Rate</span>
                      <span className="text-sm font-medium text-gray-900">
                        {/* {selectedConfig.emailsOpened ? ((selectedConfig.emailsClicked! / selectedConfig.emailsOpened) * 100).toFixed(1) : '0'}% */}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-purple-600 h-2 rounded-full" 
                        style={{ 
                          // width: `${selectedConfig.emailsOpened ? ((selectedConfig.emailsClicked! / selectedConfig.emailsOpened) * 100) : 0}%` 
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-600">Bounce Rate</span>
                      <span className="text-sm font-medium text-gray-900">
                        {/* {selectedConfig.emailsSent ? ((selectedConfig.emailsBounced! / selectedConfig.emailsSent) * 100).toFixed(1) : '0'}% */}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-orange-600 h-2 rounded-full" 
                        style={{ 
                          // width: `${selectedConfig.emailsSent ? ((selectedConfig.emailsBounced! / selectedConfig.emailsSent) * 100) : 0}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Tracking Status */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Tracking Settings</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className={`p-3 rounded-lg border ${selectedConfig.enableOpenTracking ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2">
                      <Eye className={`w-4 h-4 ${selectedConfig.enableOpenTracking ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className="text-sm font-medium text-gray-900">Open Tracking</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {selectedConfig.enableOpenTracking ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg border ${selectedConfig.enableClickTracking ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2">
                      <MousePointerClick className={`w-4 h-4 ${selectedConfig.enableClickTracking ? 'text-purple-600' : 'text-gray-400'}`} />
                      <span className="text-sm font-medium text-gray-900">Click Tracking</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {selectedConfig.enableClickTracking ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg border ${selectedConfig.enableBounceTracking ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className={`w-4 h-4 ${selectedConfig.enableBounceTracking ? 'text-orange-600' : 'text-gray-400'}`} />
                      <span className="text-sm font-medium text-gray-900">Bounce Tracking</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {selectedConfig.enableBounceTracking ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border bg-gray-50 border-gray-200">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-900">Retry Settings</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {selectedConfig.retryAttempts} attempts, {selectedConfig.retryDelay}min delay
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => setShowDetailsModal(false)}
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Logs Modal */}
      {showLogsModal && selectedConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b border-gray-100 sticky top-0 bg-white z-10">
              <CardTitle style={{ color: '#5B9BD5' }} className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Email Logs - {selectedConfig.process}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-3">
                {/* Mock log entries */}
                {[
                  { time: '2025-01-05 14:32:15', status: 'delivered', recipient: 'operator@example.com', event: 'Email Sent' },
                  { time: '2025-01-05 14:33:02', status: 'opened', recipient: 'operator@example.com', event: 'Email Opened' },
                  { time: '2025-01-05 14:35:45', status: 'clicked', recipient: 'operator@example.com', event: 'Link Clicked' },
                  { time: '2025-01-05 13:15:30', status: 'delivered', recipient: 'admin@fhims.com', event: 'Email Sent' },
                  { time: '2025-01-05 13:20:12', status: 'opened', recipient: 'admin@fhims.com', event: 'Email Opened' },
                  { time: '2025-01-05 12:45:22', status: 'bounced', recipient: 'invalid@example.com', event: 'Email Bounced' },
                ].map((log, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className={`w-3 h-3 rounded-full ${
                      log.status === 'delivered' ? 'bg-blue-500' :
                      log.status === 'opened' ? 'bg-green-500' :
                      log.status === 'clicked' ? 'bg-purple-500' :
                      'bg-red-500'
                    }`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{log.event}</span>
                        <Badge variant="outline" className="text-xs">{log.status}</Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{log.recipient}</p>
                    </div>
                    <div className="text-xs text-gray-500">{log.time}</div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-6">
                <Button
                  onClick={() => setShowLogsModal(false)}
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
